import { useDispatch, useSelector } from 'react-redux';
import { updateSettingStyle } from 'editor/redux/modules/settingStyle';
import { updateSettingTheme } from 'editor/redux/modules/settingTheme';
import { generateUpdatePayload } from 'editor/utils/promoSettingsUpdatePayloadHandler';
import { useMemo, useEffect } from 'react';
import { getForegroundColor } from 'utils/colorConvert';
import { warning, white } from 'Mui/styles/color';
import { convertSettingStyle2ComponentStyle } from 'client/styles/common';
import { system900 } from 'Mui/styles/color';
import { isPurePrizeGames, gameTypes } from 'config/promoGameTypes';
import { convertRGBtoRGBA } from 'utils/colorConvert';
import { defaultPopupColor } from 'config/promoDefaultSettingValue';
/**
 * 取得活動的主題樣式設定，每個模組必定含有這些樣式設定
 * 整合舊的 settingTheme 和新的 settingStyle，確保兼容性
 * @returns {Object} 整合後的主題樣式設定
 */

export default function usePromoSettingThemeStyle() {
    const settingTheme = useSelector(s => s.settingTheme);
    const settingStyle = useSelector(s => s.settingStyle);

    return useMemo(() => {
        const { bgColor, btnColor, textColor } = settingTheme;

        return {
            contentBgColor: bgColor || settingStyle.contentBgColor || convertRGBtoRGBA('#fff', 0.5),
            buttonBgColor: btnColor || settingStyle.buttonBgColor || '#ff004e',
            titleColor: textColor || settingStyle.titleColor || system900.color,
            buttonTextColor: settingStyle.buttonTextColor || '#fff'
        };
    }, [settingTheme, settingStyle]);
}
/**
 * 整合主題樣式的更新
 * 在編輯器中，處理從舊版 settingTheme 到新版 settingStyle 的遷移
 * @returns {Function} 處理樣式更新的函數
 */

export function useThemeStyleIntegration() {
    const dispatch = useDispatch();
    const settingTheme = useSelector(s => s.settingTheme);

    const handleStyleUpdate = async payload => {
        // 檢查舊活動是否還存在settingTheme，如果有就刪除，未來統一使用settingStyle
        const hasThemeValues = Object.values(settingTheme || {}).some(value => value !== undefined);
        if (hasThemeValues) {
            const clearedTheme = {
                bgColor: undefined,
                btnColor: undefined,
                textColor: undefined
            };
            const updatePayload = generateUpdatePayload(settingTheme, clearedTheme);
            await dispatch(updateSettingTheme(updatePayload));
        } else {
            dispatch(updateSettingStyle(payload));
        }
    };

    return handleStyleUpdate;
}

/**
 * 處理給獎遊戲的遮罩(contentBgColor)設定
 * 修正舊活動可能被預設遮罩(#fff)影響背景模式為圖片顯示的問題
 * @returns {Object} 更新後的遮罩設定
 */

export const useContentBgColorIntegration = () => {
    const dispatch = useDispatch();
    const { type: gameType } = useSelector(s => s.promo);
    const { contentBgColor } = useSelector(state => state.settingStyle);
    const isPrizeGameType = isPurePrizeGames(gameType);
    if (isPrizeGameType && (contentBgColor === '#fff' || contentBgColor === warning.color)) {
        dispatch(updateSettingStyle({ contentBgColor: 'transparent' }));
    }
    return {
        contentBgColor
    };
};

/**
 * 用於處理Instagram模組的樣式key轉換
 * @returns {Object}  轉換後的樣式對象
 */

export const useInstagramComponentStyle = () => {
    const settingStyle = useSelector(state => state.settingStyle);
    return useMemo(() => {
        const computedStyle = { ...settingStyle };
        if (!computedStyle.titleColor && !computedStyle.optionBgColor) {
            computedStyle.optionBgColor = computedStyle.contentBgColor;
            computedStyle.contentBgColor = computedStyle.layoutBgColor;
            computedStyle.titleColor = computedStyle.titleTextColor;
            computedStyle.optionTextColor = computedStyle.contentTextColor;
        }
        return convertSettingStyle2ComponentStyle(computedStyle);
    }, [settingStyle]);
};

/**
 * 用於處理關卡獎顯示的樣式key轉換
 * @param {Object} settingStyle - 外部傳入的樣式對象
 * @returns {Object} 轉換後的樣式對象
 */

export const useLotteryTaskComponentStyle = settingStyle => {
    const { type: gameType } = useSelector(s => s.promo);
    return useMemo(() => {
        if (!settingStyle) return {};
        const computedStyle = { ...settingStyle };
        if (!computedStyle.lotteryTaskTextColor && !computedStyle.lotteryTaskBgColor) {
            computedStyle.lotteryTaskTextColor = computedStyle.optionTextColor;
            computedStyle.lotteryTaskBgColor = computedStyle.optionBgColor;
            if (
                gameTypes.MULTI_CHALLENGE === gameType ||
                gameTypes.CHALLENGE_PHOTO_FRAME_COMPOSITE === gameType
            ) {
                computedStyle.optionTextColor = computedStyle.buttonTextColor;
                computedStyle.optionBgColor = computedStyle.buttonBgColor;
            }
        }
        return convertSettingStyle2ComponentStyle(computedStyle);
    }, [settingStyle, gameType]);
};

/**
 * 用於處理SettingStylePanel的key轉換並並更新
 * @returns {Object} 轉換後的樣式對象
 */

export const useSettingStylePanelComponentStyle = () => {
    const dispatch = useDispatch();
    const { type: gameType } = useSelector(s => s.promo);
    const settingStyle = useSelector(state => state.settingStyle);
    const settingTheme = useSelector(s => s.settingTheme);

    useEffect(() => {
        const updateObj = {};
        if (!settingStyle.lotteryTaskTextColor && !settingStyle.lotteryTaskBgColor) {
            // 針對多元任務及圖片闖關轉移 key
            if (
                gameTypes.MULTI_CHALLENGE === gameType ||
                gameTypes.CHALLENGE_PHOTO_FRAME_COMPOSITE === gameType
            ) {
                updateObj.lotteryTaskTextColor = settingStyle.optionTextColor;
                updateObj.lotteryTaskBgColor = settingStyle.optionBgColor;
                updateObj.optionTextColor = settingStyle.buttonTextColor;
                updateObj.optionBgColor = settingStyle.buttonBgColor;
                updateObj.buttonBgColor = settingTheme.btnColor || '#ff004e';
                updateObj.buttonTextColor = settingTheme.btnColor
                    ? getForegroundColor(settingTheme.btnColor)
                    : white.color;
            }
            // 針對邀請好友任務和簽到模組轉移 key
            else if (gameTypes.MGM_SHARE === gameType || gameTypes.CHECK_IN === gameType) {
                updateObj.lotteryTaskTextColor = settingStyle.optionTextColor;
                updateObj.lotteryTaskBgColor = settingStyle.optionBgColor;
            }

            // 針對Instagram 模組轉移 key
            if (gameTypes.INSTAGRAM === gameType) {
                if (!settingStyle.titleColor && !settingStyle.optionBgColor) {
                    updateObj.optionBgColor = settingStyle.contentBgColor;
                    updateObj.contentBgColor = settingStyle.layoutBgColor;
                    updateObj.titleColor = settingStyle.titleTextColor;
                    updateObj.optionTextColor = settingStyle.contentTextColor;
                }
            }

            // 通用樣式處理
            if (!settingStyle.titleColor) {
                updateObj.titleColor = settingTheme.textColor || system900.color;
            }

            if (!settingStyle.buttonBgColor) {
                updateObj.buttonBgColor = settingTheme.btnColor || '#ff004e';
            }

            if (!settingStyle.buttonTextColor) {
                updateObj.buttonTextColor = settingTheme.btnColor
                    ? getForegroundColor(settingTheme.btnColor)
                    : white.color;
            }
        }
        //[86etz0e4g]: 新增提示視窗的樣式
        Object.entries(defaultPopupColor).forEach(([key, defaultValue]) => {
            if (!settingStyle[key]) {
                updateObj[key] = defaultValue;
            }
        });
        if (Object.keys(updateObj).length > 0) {
            dispatch(updateSettingStyle(updateObj));
        }
    }, [dispatch, gameType, settingStyle, settingTheme]);

    return useMemo(() => {
        return convertSettingStyle2ComponentStyle(settingStyle);
    }, [settingStyle]);
};
